import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  Broker,
  BrokerProgress,
  CreateBrokerRequest,
  MergerFilter
} from "../../../api/Api";
import { buildProgressOverride } from "../../../components/lifecycle/progressOverride/buildProgressOverride";

export type BrokerStatusFilter = "active" | "archived" | "all";

export const brokerKeys = {
  all: ["brokers"] as const,
  overviews: () => [...brokerKeys.all, "overview"] as const,
  overview: (
    status: BrokerStatusFilter = "active",
    merger: MergerFilter = "exclude"
  ) => [...brokerKeys.overviews(), status, merger] as const,
  completedOverview: (merger: MergerFilter = "exclude") =>
    [...brokerKeys.overviews(), "completed", merger] as const,
  specific: (id: string) => [...brokerKeys.all, "specific", id] as const,
  assignees: (groupName: string) =>
    [...brokerKeys.all, "assignees", groupName] as const
};

// Mergers live in their own tab, so the Brokers grids leave them out (the backend default too).
export const useBrokerOverview = (
  status: BrokerStatusFilter = "active",
  merger: MergerFilter = "exclude"
) => {
  return useQuery({
    queryKey: brokerKeys.overview(status, merger),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerOverview.brokerOverview({
        status,
        merger
      });
      return response.data;
    }
  });
};

export const useCompletedBrokerOverview = (
  merger: MergerFilter = "exclude"
) => {
  return useQuery({
    queryKey: brokerKeys.completedOverview(merger),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response =
        await apiClient.completedBrokerOverview.completedBrokerOverview({
          merger
        });
      return response.data;
    }
  });
};

export const useSpecificBroker = (
  id?: string,
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: brokerKeys.specific(id ?? "not_set"),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerSpecific.brokerSpecific({
        id: id ?? "id_should_always_exist"
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!id,
    refetchInterval: options?.refetchInterval
  });
};

export const useBrokerAssignees = (groupName: string) => {
  return useQuery({
    queryKey: brokerKeys.assignees(groupName),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerAssignees.brokerAssignees({
        groupName
      });
      return response.data;
    },
    enabled: !!groupName
  });
};

//mutations

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useCreateBroker = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    // Resolves to the new broker's id (the merger form uploads attachments to it).
    mutationFn: async (request: CreateBrokerRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerCreate.brokerCreate(request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      if (options?.onSuccess) {
        options.onSuccess();
      }
    }
  });
};

export const useArchiveBroker = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerArchive.brokerArchive({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRestoreBroker = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerRestore.brokerRestore({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateBroker = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async (broker: Broker) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.brokerUpdate.brokerUpdate(broker);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.id) {
        //optimistic update of the specific broker query data
        queryClient.setQueryData(brokerKeys.specific(variables.id), variables);
        // Then refetch: the server stamps a new version on every save and merges the next save
        // against the version it carries. Keeping the one sent here would make undoing your own
        // earlier change look like no change (ADHO-496).
        queryClient.invalidateQueries({
          queryKey: brokerKeys.specific(variables.id)
        });
      }

      queryClient.invalidateQueries({
        queryKey: brokerKeys.overviews()
      });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error as Error);
      }
    }
  });
};

/** The admin progress dropdown in a broker lifecycle dialog. Its own mutation, so it never closes the dialog. */
export const useBrokerProgressOverride = (
  broker: Broker,
  progressKey: keyof BrokerProgress
) => buildProgressOverride(broker, progressKey, useUpdateBroker());
